import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ForbiddenIcon, CheckIcon } from '@rsos/assets/icons';
import { Button, Text } from '@rsos/components/capstone/base';
import {
  ACCEPTED,
  DECLINED,
  IGNORED,
  TIMEOUT,
} from '@rsos/constants/alertStatuses';
import { updateAlertStatus } from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import { addFollower } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { trackAlertChatButtonClick } from './alertChatBoxTracking';

/**
 * Sub-component of AlertChatBox that displays the alert accept and decline buttons.
 * @param {Function} onShowDeclinePane - Determine if the alert decline options pane should be displayed
 */
const AcceptDeclineAlert = ({ onShowDeclinePane }) => {
  const dispatch = useDispatch();

  const {
    currentOrg,
    profile: { id: userID },
  } = useSelector(state => state.sinatra.user);

  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);

  const currentAlert = useSelectedAlert();
  const isTestAlert = !!currentAlert?.test_mode?.test_enabled;

  const selectedAlertID = currentAlert?.alert_id;

  const alertStatus = currentAlert?.status?.name;

  const isSupplementalOnly = currentAlert?.supplemental_only;

  const [isRequestPending, setIsRequestPending] = useState(false);

  const isButtonDisabled = useMemo(
    () =>
      [DECLINED, IGNORED, TIMEOUT].includes(alertStatus) || isRequestPending,
    [alertStatus, isRequestPending],
  );

  const handleAcceptAlert = () => {
    setIsRequestPending(true);

    const data = {
      alertID: selectedAlertID,
      org: currentOrg.name,
      status: ACCEPTED,
    };

    dispatch(updateAlertStatus(data)).finally(() => setIsRequestPending(false));

    dispatch(addFollower(userID));

    trackAlertChatButtonClick({
      buttonText: ACCEPTED,
      isSupplemental: isSupplementalOnly,
      selectedAlertID,
      irpVersion,
    });
  };

  const handleDeclineAlert = () => {
    onShowDeclinePane(true);

    trackAlertChatButtonClick({
      buttonText: DECLINED,
      isSupplemmental: isSupplementalOnly,
      selectedAlertID,
      irpVersion,
    });
  };

  return (
    <>
      <Button
        btnType="primary"
        disabled={isButtonDisabled}
        name="alarm-accept-btn"
        onClick={handleAcceptAlert}
        size="small"
      >
        <CheckIcon width={20} height={20} />
        <Text inline size="small">
          {isTestAlert ? 'Accept Test' : 'Accept'}
        </Text>
      </Button>
      <Button
        btnType="tertiary"
        disabled={isButtonDisabled}
        name="alarm-decline-btn"
        onClick={handleDeclineAlert}
        size="small"
      >
        <ForbiddenIcon width={16} height={16} />
        <Text size="small"> {isTestAlert ? 'Decline Test' : 'Decline'}</Text>
      </Button>
    </>
  );
};

AcceptDeclineAlert.propTypes = {
  onShowDeclinePane: PropTypes.func.isRequired,
};

export default AcceptDeclineAlert;
